import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    console.log('AuthContext: Checking auth status...');
    try {
      const token = localStorage.getItem('auth_token');
      console.log('AuthContext: Token found:', !!token);
      if (token) {
        console.log('AuthContext: Calling getUser API...');
        const response = await authAPI.getUser();
        console.log('AuthContext: getUser response:', response);
        if (response.status === 'success') {
          console.log('AuthContext: Setting user and authenticated state');
          setUser(response.data.user);
          setIsAuthenticated(true);
        }
      }
    } catch (error) {
      console.error('AuthContext: Auth check failed:', error);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    } finally {
      console.log('AuthContext: Setting loading to false');
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    console.log('AuthContext: Login called with:', credentials);
    try {
      const response = await authAPI.login(credentials);
      console.log('AuthContext: Login API response:', response);
      
      if (response.status === 'success') {
        const { user, token } = response.data;
        console.log('AuthContext: Storing token and user data');
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
        console.log('AuthContext: Login successful, state updated');
        return { success: true, data: response.data };
      } else if (response.status === 'otp_required') {
        console.log('AuthContext: OTP verification required');
        return { 
          success: false, 
          requiresOtp: true, 
          otpData: response.data,
          message: response.message 
        };
      } else if (response.status === 'suspended') {
        console.log('AuthContext: Account suspended');
        return { 
          success: false, 
          isSuspended: true,
          error: response.message || 'Account suspended',
          suspensionData: response.data
        };
      } else {
        return { 
          success: false, 
          error: response.message || 'Login failed',
        };
      }
    } catch (error) {
      console.error('AuthContext: Login failed:', error);
      const errorData = error.response?.data;
      return { 
        success: false, 
        error: errorData?.message || 'Login failed',
        errors: errorData?.errors || null
      };
    }
  };

  const register = async (userData) => {
    try {
      console.log('AuthContext: Register called with data:', userData);
      const response = await authAPI.register(userData);
      console.log('AuthContext: Register API response:', response);
      if (response.status === 'success') {
        const { user, token } = response.data;
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      const errorData = error.response?.data;
      console.log('Error response data:', errorData);
      console.log('Validation errors:', errorData?.errors);
      return { 
        success: false, 
        error: errorData?.message || 'Registration failed',
        errors: errorData?.errors || null
      };
    }
  };

  const verifyOtp = async (otpData) => {
    console.log('AuthContext: OTP verification called with:', otpData);
    try {
      const response = await authAPI.verifyOtp(otpData);
      console.log('AuthContext: OTP verification response:', response);
      
      if (response.status === 'success') {
        const { user, token } = response.data;
        console.log('AuthContext: OTP verified, storing token and user data');
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(user));
        setUser(user);
        setIsAuthenticated(true);
        console.log('AuthContext: OTP verification successful, state updated');
        return { success: true, data: response.data };
      } else {
        return { 
          success: false, 
          error: response.message || 'OTP verification failed',
          code: response.code || null,
          remainingAttempts: response.remaining_attempts || null
        };
      }
    } catch (error) {
      console.error('AuthContext: OTP verification failed:', error);
      const errorData = error.response?.data;
      return { 
        success: false, 
        error: errorData?.message || 'OTP verification failed',
        code: errorData?.code || null,
        remainingAttempts: errorData?.remaining_attempts || null
      };
    }
  };

  const resendOtp = async (otpData) => {
    console.log('AuthContext: OTP resend called with:', otpData);
    try {
      const response = await authAPI.resendOtp(otpData);
      console.log('AuthContext: OTP resend response:', response);
      
      if (response.status === 'success') {
        return { success: true, data: response.data };
      } else {
        return { 
          success: false, 
          error: response.message || 'Failed to resend OTP'
        };
      }
    } catch (error) {
      console.error('AuthContext: OTP resend failed:', error);
      const errorData = error.response?.data;
      return { 
        success: false, 
        error: errorData?.message || 'Failed to resend OTP'
      };
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      if (response.status === 'success') {
        setUser(response.data.user);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        return { success: true, data: response.data };
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      const errorData = error.response?.data;
      return { 
        success: false, 
        error: errorData?.message || 'Profile update failed',
        errors: errorData?.errors || null
      };
    }
  };

  const changePassword = async (passwordData) => {
    try {
      const response = await authAPI.changePassword(passwordData);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Password change failed:', error);
      const errorData = error.response?.data;
      return { 
        success: false, 
        error: errorData?.message || 'Password change failed',
        errors: errorData?.errors || null
      };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    register,
    logout,
    verifyOtp,
    resendOtp,
    updateProfile,
    changePassword,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 